import React, { useState, useEffect, useCallback } from "react";
import { ChantInput } from "../../../../CommonComponents";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Input, Checkbox, Divider } from "antd";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import useAdminDataStore, { selectCurrentGroupData, selectCurrentGroupId } from "../../../../store/adminData/adminData";
import {
  fbCreateChant,
  fbUpdateChant,
  fbGetChant,
} from "../../../../services/firebaseService/endPoints/admin/ourChants";
import "./ourChants.css";
import { applicationError } from "../../../../redux/actions/error";
import { invalidateStadiumCache, postUpdateCDNUtil } from "../../../../helperFunctions/util";
import { ChantButton, ScreenHeader } from "../../../../CommonComponents";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ChantContainer from "../../../../CommonComponents/ChantContainer";

const CreateChant = () => {
  const location = useLocation();
  const { id: chantId } = useParams();
  const navigate = useNavigate();

  const dashboardType = location.pathname.split("/")[1];

  const [lyrics, setLyrics] = useState("");
  const [noteEditorState, setNoteEditorState] = useState(
    EditorState.createEmpty()
  );
  const [title, setTitle] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [checked, setChecked] = useState(false);

  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const onEditorStateChange = (editorState, name) => {
    if (name === "noteEditorState") {
      setNoteEditorState(editorState);
    }
    // html: draftToHtml(convertToRaw(editorState.getCurrentContent()))
  };

  const handleInputChange = (e) => {
    if (e.target.name === "title") {
      setTitle(e.target.value);
    } else if (e.target.name === "lyrics") {
      setLyrics(e.target.value);
    } else if (e.target.name === "videoLink") {
      setVideoLink(e.target.value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (checked === true) {
      const result = {
        title: title,
        lyrics: lyrics,
        specialNote: draftToHtml(
          convertToRaw(noteEditorState.getCurrentContent())
        ),
        videoLink: videoLink,
      };

      if (dashboardType === "clubadmin") {
        result.type = "global";
        result.groups = ["global"];
      }

      setIsLoading(true);
      let promise;

      if (editMode) {
        promise = fbUpdateChant(currentGroup, chantId, result);
      } else {
        promise = fbCreateChant(currentGroup, result);
      }

      promise.then(() => {
        setIsLoading(false);
        navigate(`/${dashboardType}/chants`, { replace: true });
        postUpdateCDNUtil(currentGroup);
        invalidateStadiumCache(groupData?.groupKey);
      });
    } else {
      alert("Accept the Terms & Conditions");
    }
  };

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleEditMode = useCallback(() => {
    fbGetChant(currentGroup, chantId).then((data) => {
      if (data) {
        const contentBlockNote = htmlToDraft(data.specialNote);

        if (contentBlockNote) {
          const contentState = ContentState.createFromBlockArray(
            contentBlockNote.contentBlocks
          );
          setNoteEditorState(EditorState.createWithContent(contentState));
        }

        setTitle(data.title);
        setLyrics(data.lyrics);
        setVideoLink(data.videoLink);
        setIsLoading(false);
        setEditMode(true);
      } else {
        applicationError({ err: {}, errorCode: "404" });
        return;
      }
    });
  }, [currentGroup, chantId]);

  useEffect(() => {
    if (chantId) {
      handleEditMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chantId]);

  return (
    <ChantContainer>
      <ScreenHeader title="Create Chant" />

      <div>
        <form
          onSubmit={handleSubmit}
          style={{
            padding: "12px",
            marginTop: "var(--screen-general-margin)",
            background: "var(--primary-white-background)",
            // boxShadow: "0px 3px 10px 1px",
          }}
        >
          <div className="chant-form-body">
            <ChantInput
              label="Title"
              name="title"
              placeholder="Enter Title"
              onChange={handleInputChange}
              value={title}
              required
            />
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "6px" }}>Lyrics</label>
              <Input.TextArea
                style={{ fontSize: "18px" }}
                placeholder="Enter Lyrics"
                value={lyrics}
                name="lyrics"
                onChange={handleInputChange}
                rows={8}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "6px" }}>Special Note</label>
              <Editor
                editorState={noteEditorState}
                wrapperClassName="border rounded p-3 mt-0 flex-1"
                editorClassName="height-fit-content"
                placeholder="Enter special note"
                onEditorStateChange={(editorState) => {
                  onEditorStateChange(editorState, "noteEditorState");
                }}
                toolbar={{
                  options: ["inline", "blockType", "list", "colorPicker", "link"],
                  inline: {
                    options: ["bold", "italic", "underline", "strikethrough"],
                  },
                  blockType: {
                    options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
                  },
                  link: { defaultTargetOption: "_blank" },
                }}
                stripPastedStyles={true}
              />
            </div>
            <ChantInput
              label="Video Link"
              name="videoLink"
              placeholder="Enter YouTube link"
              onChange={handleInputChange}
              value={videoLink}
            />
          </div>

          <div
            className="col"
            style={{
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
          >
            <Checkbox
              style={{ marginLeft: "18px", marginBottom: "16px" }}
              checked={checked}
              onChange={handleChange}
              value="secondary"
              color="primary"
              aria-label="Terms and conditions checkbox"
            />
            <span
              style={{
                marginLeft: "15px",
                fontsize: "50px",
                color: "#FF0000",
              }}
            >
              *{" "}
            </span>
            <p style={{ marginLeft: "5px" }}>
              I confirm that this chant does not violate the code of conduct of
              my club, the league or the terms of Chant. This chant is in no way
              racist, homophobic, misogynistic, bigoted or otherwise aggressive.
            </p>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          <div className="d-flex justify-content-center mt-2">
            <ChantButton
              type="primary"
              onClick={handleSubmit}
              disabled={!checked || !title}
              loading={isLoading}
            >
              Submit
            </ChantButton>
          </div>
        </form>
      </div>
    </ChantContainer>
  );
};

export default CreateChant;
